import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import {
  InvestorDto,
  PartialInvestorDto,
} from "../../../../data/dtos/investor.dto";
import { EntityInformationEditableCard } from "./EntityInformationEditableCard";
import { WhmStack } from "@securitize/reactjs-whm";
import { BusinessInformationEditableCard } from "./BusinessInformationEditableCard";
import { AddressEditableCard } from "./AddressEditableCard";
import { isEurope, isUS } from "../../../Registration/countriesList";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import TranslationKeys from "../../../../contexts/I18nContext/translationKeys";
import translationKeys from "../../../../contexts/I18nContext/translationKeys";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import { useUpdateInvestorMutation } from "../../../../data/useProfile";
import { SnackbarContext } from "../../../shared/Snackbar";
import { Form, Formik } from "formik";
import { createValidationSchema, EntityKYBFormType } from "./validations";
import { areCountryCodeAndStateValid } from "../../../../utils/validations";
import {
  getValidValuesFromYupError,
  hasAnyDifferentValue,
  trimValues,
} from "../../../../utils/formikUtils";
import * as Yup from "yup";
import { I18nContext } from "../../../../contexts/I18nContext";
import { SaveFooter } from "../../shared/SaveFooter";
import { useGetOnboardingData } from "../../../../data/usePortfolio";
import { getFlaggedFieldsByFormikName } from "./entityFieldKeyMap";
import useUserInfo from "../../../../hooks/useUserInfo";

interface EntityInformationFormProps {
  investorInfoQuery: UseQueryResult<InvestorDto, Error>;
  kybPending: boolean;
}

export const EntityInformationForm: React.FC<
  React.PropsWithChildren<EntityInformationFormProps>
> = ({ investorInfoQuery, kybPending }) => {
  const { getTranslation } = useContext(I18nContext);
  const userInfo = useUserInfo();

  const initialValues = React.useMemo(
    () => ({
      entityInformation: {
        entityName: investorInfoQuery.data?.details.entityName || "",
        entityType: investorInfoQuery.data?.details.entityType,
        incorporationDate: investorInfoQuery.data?.details.incorporationDate,
        entityIdNumber: investorInfoQuery.data?.details.entityIdNumber || "",
        businesses: investorInfoQuery.data?.details.businesses || "",
      },
      businessInformation: {
        website: investorInfoQuery.data?.details.website || "",
        leiCode: investorInfoQuery.data?.details.leiCode || "",
        sourceOfFunds: investorInfoQuery.data?.details.sourceOfFunds || "",
        lineOfBusiness: investorInfoQuery.data?.details.lineOfBusiness || "",
      },
      address: {
        street: investorInfoQuery.data?.details.address?.street || "",
        entrance: investorInfoQuery.data?.details.address?.entrance || "",
        city: investorInfoQuery.data?.details.address?.city || "",
        zip: investorInfoQuery.data?.details.address?.zip || "",
        countryCode: investorInfoQuery.data?.details.address?.countryCode,
        state: isUS(investorInfoQuery.data?.details.address?.countryCode || "")
          ? investorInfoQuery.data?.details.address?.state
          : undefined,
      },
    }),
    [investorInfoQuery.data],
  );

  const [previousValues, setPreviousValues] =
    React.useState<EntityKYBFormType>(initialValues);

  const { showSnackbar } = useContext(SnackbarContext);
  const queryClient = useQueryClient();
  const updateInvestorMutation = useUpdateInvestorMutation();
  const onboardingQuery = useGetOnboardingData();
  const flaggedFields = useMemo(
    () =>
      getFlaggedFieldsByFormikName(
        onboardingQuery.data?.requestedUpdatesGrouped
          ?.entityInformationComments,
      ),
    [onboardingQuery.data?.requestedUpdatesGrouped?.entityInformationComments],
  );
  const requestedUpdatesRoundId =
    onboardingQuery.data?.requestedUpdatesGrouped?.requestedAt;

  const [investor, setInvestor] = useState<PartialInvestorDto | undefined>({
    details: {
      ...investorInfoQuery.data?.details,

      // remove some unwanted fields from the initial state
      netWorth: undefined,
      annualIncome: undefined,
    },
  });
  const [isInvestorEu, setIsInvestorEu] = useState(
    isEurope(investor?.details?.address?.countryCode || ""),
  );
  const validationSchema = useMemo(
    () => createValidationSchema(getTranslation, isInvestorEu),
    // This rule is specific to re-run the validation on leiCode once the isInvestorEu changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isInvestorEu],
  );
  const shouldSave = useRef(false);

  const updateInvestor = useCallback(
    (newInvestor: PartialInvestorDto) => {
      updateInvestorMutation.mutate(newInvestor, {
        onSuccess: () => {
          // Invalidate onboarding data to refresh the onboarding card
          void queryClient.invalidateQueries({ queryKey: ["onboarding"] });
          // Invalidate pending actions to refresh the badge
          void queryClient.invalidateQueries({ queryKey: ["pending-actions"] });
        },
        onError: (error) => {
          showSnackbar({
            severity: "error",
            message: <ErrorSnackbar error={error} />,
          });
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateInvestorMutation.mutate, showSnackbar, queryClient],
  );

  useEffect(() => {
    if (shouldSave.current && investor) updateInvestor(investor);
  }, [investor, updateInvestor]);

  useEffect(() => {
    // used to avoid the initial update of the investor on first render
    // once component is mounted we start listening to updates from the form
    shouldSave.current = true;
  }, []);

  const onValueUpdated = (values: EntityKYBFormType, forceUpdate?: boolean) => {
    const trimmedValues = trimValues<EntityKYBFormType>(values);
    setIsInvestorEu(isEurope(values.address.countryCode || ""));

    validationSchema
      .validate(trimmedValues, { abortEarly: false })
      .then((_) => {
        // All fields are valid
        updateValuesIfChanged(
          {
            entityInformation: trimmedValues.entityInformation,
            businessInformation: trimmedValues.businessInformation,
            address: trimmedValues.address,
          },
          forceUpdate,
        );
      })
      .catch((err: Yup.ValidationError) => {
        const validValues = getValidValuesFromYupError<EntityKYBFormType>(
          trimmedValues,
          err,
        );

        updateValuesIfChanged(
          {
            entityInformation: validValues.entityInformation || {},
            businessInformation: validValues.businessInformation || {},
            address: validValues.address || {},
          },
          forceUpdate,
        );
      });

    setPreviousValues(trimmedValues);
  };

  const updateValuesIfChanged = (
    validValues: EntityKYBFormType,
    forceUpdate?: boolean,
  ) => {
    const newValidValues = areCountryCodeAndStateValid(
      validValues.address.countryCode,
      validValues.address.state,
    )
      ? validValues
      : {
          ...validValues,
          address: {
            ...validValues.address,
            countryCode: undefined,
            state: undefined,
          },
        };

    if (
      hasAnyDifferentValue<EntityKYBFormType>(previousValues, newValidValues) ||
      forceUpdate
    ) {
      setInvestor((prevInvestor) => {
        if (!prevInvestor) return undefined;
        return {
          details: {
            ...prevInvestor.details,
            entityType: newValidValues.entityInformation.entityType,
            entityName: newValidValues.entityInformation.entityName,
            entityIdNumber: newValidValues.entityInformation.entityIdNumber,
            incorporationDate:
              newValidValues.entityInformation.incorporationDate,
            businesses: newValidValues.entityInformation.businesses,
            leiCode: newValidValues.businessInformation.leiCode,
            lineOfBusiness: newValidValues.businessInformation.lineOfBusiness,
            sourceOfFunds: newValidValues.businessInformation.sourceOfFunds,
            website: newValidValues.businessInformation.website,
            address: {
              ...prevInvestor.details?.address,
              state: newValidValues.address.state,
              countryCode: newValidValues.address.countryCode,
              city: newValidValues.address.city,
              street: newValidValues.address.street,
              zip: newValidValues.address.zip,
              entrance: newValidValues.address.entrance,
            },
          },
        };
      });
    }
  };

  return (
    <Formik<EntityKYBFormType>
      initialValues={initialValues}
      validationSchema={kybPending ? validationSchema : null}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={() => {}}
    >
      {(formik) => {
        return (
          <Form>
            <WhmStack gap="20px" flex="1">
              <EntityInformationEditableCard
                kybPending={kybPending}
                onNotifyValueChange={onValueUpdated}
                flaggedFields={flaggedFields}
                investorId={userInfo.investorId}
                requestedUpdatesRoundId={requestedUpdatesRoundId}
              />
              <BusinessInformationEditableCard
                kybPending={kybPending}
                isInvestorEu={isInvestorEu}
                onNotifyValueChange={onValueUpdated}
                flaggedFields={flaggedFields}
                investorId={userInfo.investorId}
                requestedUpdatesRoundId={requestedUpdatesRoundId}
              />
              <AddressEditableCard
                investorInfoQuery={investorInfoQuery}
                kybPending={kybPending}
                onNotifyValueChange={onValueUpdated}
                flaggedFields={flaggedFields}
                investorId={userInfo.investorId}
                requestedUpdatesRoundId={requestedUpdatesRoundId}
              />
              {kybPending && (
                <SaveFooter
                  isLoading={updateInvestorMutation.isPending}
                  onSave={() => onValueUpdated(formik.values, true)}
                  saveButtonText={
                    updateInvestorMutation.isPending ? (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_ENTITY_INFORMATION_CTA_LOADING
                        }
                      />
                    ) : (
                      <Translate
                        translationKey={
                          translationKeys.PROFILE_ENTITY_INFORMATION_CTA
                        }
                      />
                    )
                  }
                  footer={
                    <TranslateHtml
                      translationKey={
                        TranslationKeys.PROFILE_ENTITY_INFORMATION_FOOTER
                      }
                    />
                  }
                />
              )}
            </WhmStack>
          </Form>
        );
      }}
    </Formik>
  );
};
